import { BroadcastThumbnails, PageReloadMode, } from "@dreamcam/web-sdk";
import { Env } from "../settings/Env";
import { Scene } from "./Scene";
import { AppView } from "../babylon/AppView";
import { Logger } from "../utils/Logger";
import { VideosTab } from "../interfaces/LobbyInterfaces";
import { Settings } from "../settings/Settings";
import { globalOptions } from "../settings/GlobalOptions";
import { MessageType } from "../interfaces/NotificationCenter";
import { text } from "../TextResources";
import { waitFor } from "../utils/AsyncUtils";
export class LobbyScene extends Scene {
    get isLoadingLivePage() { return this._isLoadingLivePage; }
    get isLoadingVideosPage() { return this._isLoadingVideosPage; }
    get isLoadingPurchasesPage() { return this._isLoadingPurchasesPage; }
    get isUpdatingGalleryFilter() { return this._isUpdatingGalleryFilter; }
    constructor(appController) {
        super(appController);
        // private _currentPageIndex = 0;
        // private _totalPages = 0;
        this._gallerySize = Env.lobby.gallery.size;
        this._authListeners = [];
        this._catalogListeners = [];
        this._galleryListeners = [];
        this._isReady = false;
        this._allGalleryTags = [];
        this._lobbyMode = "Live";
        this._lobbyVideosMode = "AllVideos";
        this._thumbnails = null;
        this._videoClips = null;
        this._purchases = null;
        this._isLoadingOnShow = false;
        this._isLoadingLivePage = false;
        this._isLoadingVideosPage = false;
        this._isLoadingPurchasesPage = false;
        this._isUpdatingGalleryFilter = false;
        this._lastModelsPageIndex = undefined;
        this._liveFilterChanged = async (_args) => {
            try {
                this.view?.updateBroadcastFilterTabs(this.appController.sdk.catalogManager.filter);
                await this._loadAndDrawLivePageAsync(0);
                const tagGroups = await this.appController.sdk.catalogManager.loadTagGroupsAsync(this.appController.sdk.catalogManager.filter.categoriesArray, [], [], this.appController.sdk.catalogManager.filter.tagsArray, false, false, ["video2D", "video3D"]);
                this.view?.updateLiveTagGroups(tagGroups);
            }
            finally {
                this.view?.enableLobbyModeTabControl();
            }
        };
        this._livePageChanged = async (_args) => {
            await this._sceneCallbacks.updateCurrentPostersAsync();
        };
        this._videosFilterChanged = async (_args) => {
            try {
                this.view?.updateModelMode();
                await this._loadAndDrawTagsAsync(VideosTab.allVideos);
                await this._loadSelectedModelInfoAsync();
                await this._loadAndDrawVideosPageAsync(0);
                this._videoclipsFilteredSuccessfully = true;
                this._lastSuccessfulVideoclipsFilter = this.appController.sdk.galleryManager.filter.get();
            }
            catch (e) {
                if (this._videoclipsFilteredSuccessfully) {
                    this.appController.notificationCenter?.showMessage(MessageType.error, text.error_network_error);
                }
                this._videoclipsFilteredSuccessfully = false;
                this.appController.sdk.galleryManager.filter.set(this._lastSuccessfulVideoclipsFilter);
                try {
                    await this._loadAndDrawTagsAsync(VideosTab.allVideos);
                }
                catch (_) { /* ignore */ }
                if (this._videoClips) {
                    const totalPages = this._videosPaginationInfo.totalPages;
                    const currentPageIndex = this._videosPaginationInfo.pageIndex;
                    this.view?.drawVideoClipsPageInfo(totalPages, currentPageIndex);
                    this.view?.drawVideoClips(this._videoClips);
                }
            }
            finally {
                this._isUpdatingGalleryFilter = false;
                this.view?.enableLobbyModeTabControl();
            }
        };
        this._videosPageChanged = async (_args) => {
        };
        this._loginChanged = async () => {
            if (!this.isVisible)
                return;
            if (!this.appController.sdk.authManager.isAuthorized) {
                this._unsubscribeCatalog();
                try {
                    this.appController.sdk.catalogManager.filter.favoritesOnly = false;
                    this.appController.sdk.galleryManager.filter.isPurchasedOnly = false;
                }
                finally {
                    this._subscribeCatalog();
                }
            }
            const results = await Promise.allSettled([
                this._loadCurrentLivePageAsync(),
                this._loadCurrentVideosPageAsync(),
                this._loadModelsAsync(),
                this._updateLoginSection(),
            ]);
            results.forEach((x) => {
                if (x.status === "rejected")
                    throw (x.reason);
            });
        };
        this._updateLoginSection = async () => {
            if (!this.appController.sdk.authManager.isAuthorized) {
                this.view?.updateLoginSection();
                return Promise.resolve();
            }
            const nickname = this.appController.sdk.authManager.authResponse?.clientNickname || "";
            try {
                const tokens = await this.appController.sdk.clientProfileManager.getBalanceAsync();
                if (!this.isVisible)
                    return;
                this.view?.updateLoginSection({
                    nickname: nickname,
                    tokens: tokens
                });
            }
            catch (e) {
                if (e.code === 401) { // (unauthorized)
                    if (this.isVisible)
                        this.view?.updateLoginSection(undefined);
                }
                else {
                    throw e;
                }
            }
        };
        this._loadCurrentLivePageAsync = async () => {
            const currentPageIndex = this.appController.sdk.catalogManager.pagination?.pageIndex ?? 0;
            await this._loadAndDrawLivePageAsync(currentPageIndex);
            const tagGroups = await this.appController.sdk.catalogManager.loadTagGroupsAsync(this.appController.sdk.catalogManager.filter.categoriesArray, [], [], this.appController.sdk.catalogManager.filter.tagsArray, false, false, this.appController.sdk.catalogManager.filter.streamTypesArray);
            this.view?.updateLiveTagGroups(tagGroups);
            this.view?.updateBroadcastFilterTabs(this.appController.sdk.catalogManager.filter);
        };
        this._loadAndDrawTagsAsync = async (videosTab) => {
            if (videosTab === VideosTab.allVideos) {
                const filter = this.appController.sdk.galleryManager.filter;
                try {
                    const tags = await this.appController.sdk.galleryManager.loadGalleryTagsAsync({
                        selectedTags: filter.tags?.split(",") ?? null,
                        modelId: filter.modelId,
                        dateAfter: filter.dateAfter,
                        isPurchasedOnly: filter.isPurchasedOnly ?? false,
                    });
                    this._allGalleryTags = tags;
                    this.view?.updateVideosTags(this._allGalleryTags, VideosTab.allVideos);
                }
                catch (_) {
                    this.view?.updateVideosTags(this._allGalleryTags, VideosTab.allVideos);
                }
            }
            else {
                this.view?.updateVideosTags([], VideosTab.purchased);
            }
        };
        this._loadCurrentVideosPageAsync = async () => {
            await this._loadAndDrawVideosPageAsync(this._videosPaginationInfo.pageIndex);
            await this._loadAndDrawTagsAsync(VideosTab.allVideos);
            // this.view?.updateVideosFilterTabs(VideosTab.allVideos);
        };
        this._loadCurrentPurchasesPageAsync = async () => {
            await this._loadAndDrawPurchasesPageAsync(this._purchasesPaginationInfo.pageIndex);
            await this._loadAndDrawTagsAsync(VideosTab.purchased);
            // this.view?.updateVideosFilterTabs(VideosTab.purchased);
        };
        this._videosPaginationInfo = {
            totalItems: 0,
            pageSize: 0,
            totalPages: 1,
            pageIndex: 0,
            itemsOnPage: 0,
        };
        this._purchasesPaginationInfo = {
            totalItems: 0,
            pageSize: 0,
            totalPages: 1,
            pageIndex: 0,
            itemsOnPage: 0,
        };
        this._modelsPaginationInfo = {
            totalItems: 0,
            pageSize: 0,
            totalPages: 1,
            pageIndex: 0,
            itemsOnPage: 0,
        };
        this._loadModelsAsync = async () => {
            if (this.view === undefined)
                return;
            const pageSize = this.view.getModelsOnPage();
            const newPageIndex = 0;
            const studioModels = await this.appController.sdk.galleryManager.loadModelsPageAsync(newPageIndex, pageSize);
            this._lastModelsPageIndex = newPageIndex;
            this.view.updateModelsPage(studioModels);
            this._modelsPaginationInfo = LobbyScene._recalculateModelsPaginationInfo(pageSize, newPageIndex, studioModels);
            this.view.updateModelsPageInfo(this._modelsPaginationInfo);
        };
        this._loadSelectedModelInfoAsync = async () => {
            if (this.view === undefined)
                return;
            const studioModel = this.appController.sdk.galleryManager.filter.studioModel ?? null;
            if (studioModel === null) {
                this.view.updateSelectedModelInfo(null, null);
            }
            else {
                this.view.updateSelectedModelInfo(null, null);
                let broadcast = null;
                try {
                    broadcast = await this.appController.sdk.catalogManager.loadBroadcastByModelNicknameAsync(studioModel.modelNickname, {
                        showHidden: this.appController.showHiddenBroadcasts,
                        includeFavorites: true
                    });
                }
                catch (e) {
                    if (e.code !== 404)
                        throw e;
                }
                this.view.updateSelectedModelInfo(studioModel, broadcast);
            }
        };
        this._loadAndDrawLivePageAsync = async (pageIndex) => {
            if (!this.isVisible || pageIndex < 0 || this._isLoadingLivePage)
                return;
            this._isLoadingLivePage = true;
            try {
                const pageSize = this._gallerySize.columns * this._gallerySize.rows;
                await this._loadLivePageDataAsync(pageIndex, pageSize);
                const totalPages = this._thumbnails.totalPages;
                const currentPageIndex = this._thumbnails.pageIndex;
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                lobbyPageParams.lobbyMode = this._lobbyMode;
                // lobbyPageParams.livePageIndex = currentPageIndex;
                // lobbyPageParams.livePageSize = pageSize;
                // lobbyPageParams.liveFilter = this.appController.sdk.catalogManager.filter.tagsFilter;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
                this.view?.drawBroadcastsPageInfo(totalPages, currentPageIndex);
                this.view?.drawBroadcasts(this._thumbnails);
            }
            catch (e) {
                Logger.error(JSON.stringify(e));
            }
            finally {
                this._isLoadingLivePage = false;
            }
        };
        this._loadAndDrawVideosPageAsync = async (pageIndex) => {
            if (!this.isVisible || pageIndex < 0 || this._isLoadingVideosPage)
                return;
            this._isLoadingVideosPage = true;
            try {
                const pageSize = this._gallerySize.columns * this._gallerySize.rows;
                this._videoClips = await this.appController.sdk.galleryManager.loadVideoClipsPageAsync(pageIndex, pageSize, {
                    filter: this.appController.sdk.galleryManager.filter,
                    sorter: null,
                    paginationCallback: (pagination) => {
                        this._videosPaginationInfo = pagination;
                    },
                });
                const totalPages = this._videosPaginationInfo.totalPages;
                const currentPageIndex = this._videosPaginationInfo.pageIndex;
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                lobbyPageParams.lobbyMode = this._lobbyMode;
                // lobbyPageParams.videosPageIndex = currentPageIndex;
                // lobbyPageParams.videosPageSize = pageSize;
                // lobbyPageParams.videosFilter = this.appController.sdk.galleryManager.filter;
                // lobbyPageParams.videosSorter = this.appController.sdk.galleryManager.sorter;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
                this.view?.drawVideoClipsPageInfo(totalPages, currentPageIndex);
                this.view?.drawVideoClips(this._videoClips);
            }
            catch (e) {
                Logger.error(JSON.stringify(e));
            }
            finally {
                this._isLoadingVideosPage = false;
            }
        };
        this._loadAndDrawPurchasesPageAsync = async (pageIndex) => {
            if (!this.isVisible || pageIndex < 0 || this._isLoadingPurchasesPage)
                return;
            this._isLoadingPurchasesPage = true;
            try {
                const pageSize = this._gallerySize.columns * this._gallerySize.rows;
                this._purchases = { totalCount: 0, pageItems: [] };
                try {
                    this._purchases = await this.appController.sdk.galleryManager.loadVideoClipPurchasesPageAsync(pageIndex, pageSize, (pagination) => this._purchasesPaginationInfo = pagination);
                }
                catch (e) {
                    if (e.code !== 401) {
                        Logger.error(JSON.stringify(e));
                        return;
                    }
                }
                const totalPages = this._purchasesPaginationInfo.totalPages;
                const currentPageIndex = this._purchasesPaginationInfo.pageIndex;
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                lobbyPageParams.lobbyMode = this._lobbyMode;
                lobbyPageParams.lobbyVideosMode = this._lobbyVideosMode;
                // lobbyPageParams.videosPageIndex = currentPageIndex;
                // lobbyPageParams.videosPageSize = pageSize;
                // lobbyPageParams.videosFilter = this.appController.sdk.galleryManager.filter;
                // lobbyPageParams.videosSorter = this.appController.sdk.galleryManager.sorter;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
                this.view?.drawPurchasesPageInfo(totalPages, currentPageIndex);
                this.view?.drawPurchases(this._purchases);
            }
            finally {
                this._isLoadingPurchasesPage = false;
            }
        };
        this._tryToRestorePrivateShowAsync = async (data) => {
            if (!this.isVisible || this.isActivePrivateShow || this.isActiveSpyMode)
                return Promise.resolve();
            if (!data.existingShow || !this.isVisible)
                return Promise.resolve();
            // this.appController.sdk.galleryManager.closeVideoClip();
            // await this.appController.scenesSwitcher.openBroadcastAsync({
            //     broadcastId: data.existingShow.broadcastId,
            //     existingPrivateShow: data.existingShow
            // });
            const timeout = 10_000;
            await waitFor(() => !this._isLoadingLivePage, timeout, { logErrorOnTimeout: true, rejectOnTimeout: false });
            await waitFor(() => !this._isLoadingVideosPage, timeout, { logErrorOnTimeout: true, rejectOnTimeout: false });
            await waitFor(() => !this._isLoadingPurchasesPage, timeout, { logErrorOnTimeout: true, rejectOnTimeout: false });
            await waitFor(() => !this._isUpdatingGalleryFilter, timeout, { logErrorOnTimeout: true, rejectOnTimeout: false });
            const openBroadcastParams = { existingPrivateShow: data.existingShow };
            this.appController.scenesSwitcher.setBroadcastPageParams(openBroadcastParams);
            await this.appController.scenesSwitcher.openBroadcastAsync(openBroadcastParams);
            await this.view?.hideAsync();
            this.appController.scenesSwitcher.setLobbyPageParams({ lobbyMode: "Live" });
            if (this._thumbnails !== null) {
                this._thumbnails.dispose();
                this._thumbnails = null;
            }
        };
        this._sceneCallbacks = {
            isAuthorized: () => {
                return this.appController.sdk.authManager.isAuthorized;
            },
            isUpdatingGalleryFilter: () => {
                return this._isUpdatingGalleryFilter;
            },
            getSize: () => {
                return this._gallerySize;
            },
            setSizeAsync: async (size) => {
                this._gallerySize = size;
                await this.view?.initAsync(this._sceneCallbacks);
                await this._loadAndDrawLivePageAsync(0);
                await this._loadAndDrawVideosPageAsync(0);
            },
            logoutAsync: async () => {
                await this.appController.logoutAsync();
            },
            getLobbyMode: () => {
                return this._lobbyMode;
            },
            setLobbyMode: (value) => {
                this._lobbyMode = value;
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                lobbyPageParams.lobbyMode = value;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
            },
            getLobbyVideosMode: () => {
                return this._lobbyVideosMode;
            },
            setLobbyVideosMode: (value) => {
                this._lobbyVideosMode = value;
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                if (lobbyPageParams.lobbyMode === undefined)
                    lobbyPageParams.lobbyMode = "Videos";
                lobbyPageParams.lobbyVideosMode = value;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
            },
            getLivePaginationInfo: () => {
                return {
                    totalPages: this._thumbnails?.totalPages ?? 1,
                    currentPageIndex: this._thumbnails?.pageIndex ?? 0,
                };
            },
            nextLivePageAsync: async () => {
                const totalPages = this._thumbnails.totalPages;
                const currentPageIndex = this._thumbnails.pageIndex;
                const pageIndex = (currentPageIndex + 1) % totalPages;
                await this._loadAndDrawLivePageAsync(pageIndex);
                // await this._updatePosters();
            },
            previousLivePageAsync: async () => {
                const totalPages = this._thumbnails.totalPages;
                const currentPageIndex = this._thumbnails.pageIndex;
                const pageIndex = currentPageIndex === 0 ? totalPages - 1 : currentPageIndex - 1;
                await this._loadAndDrawLivePageAsync(pageIndex);
            },
            openLivePageAsync: async (pageIndex, _filter) => {
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                lobbyPageParams.lobbyMode = this._lobbyMode;
                // lobbyPageParams.livePageIndex = pageIndex;
                // lobbyPageParams.liveFilter = filter;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
                await this._loadAndDrawLivePageAsync(pageIndex);
            },
            getCatalogFilter: () => {
                return this.appController.sdk.catalogManager.filter;
            },
            getVideosPaginationInfo: () => {
                return {
                    totalPages: this._videosPaginationInfo.totalPages,
                    currentPageIndex: this._videosPaginationInfo.pageIndex,
                };
            },
            nextVideosPageAsync: async () => {
                const totalPages = this._videosPaginationInfo.totalPages;
                const currentPageIndex = this._videosPaginationInfo.pageIndex;
                const pageIndex = (currentPageIndex + 1) % totalPages;
                await this._loadAndDrawVideosPageAsync(pageIndex);
            },
            previousVideosPageAsync: async () => {
                const totalPages = this._videosPaginationInfo.totalPages;
                const currentPageIndex = this._videosPaginationInfo.pageIndex;
                const pageIndex = currentPageIndex === 0 ? totalPages - 1 : currentPageIndex - 1;
                await this._loadAndDrawVideosPageAsync(pageIndex);
            },
            openVideosPageAsync: async (pageIndex, _filter, _sorter) => {
                // this._lobbyMode = "Videos";
                // this._lobbyVideosMode = "AllVideos";
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                lobbyPageParams.lobbyMode = this._lobbyMode;
                lobbyPageParams.lobbyVideosMode = this._lobbyVideosMode;
                // lobbyPageParams.videosPageIndex = pageIndex;
                // lobbyPageParams.videosFilter = filter;
                // lobbyPageParams.videosSorter = sorter;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
                await this._loadAndDrawVideosPageAsync(pageIndex);
            },
            getPurchasesPaginationInfo: () => {
                return {
                    totalPages: this._purchasesPaginationInfo.totalPages,
                    currentPageIndex: this._purchasesPaginationInfo.pageIndex,
                };
            },
            nextPurchasesPageAsync: async () => {
                const totalPages = this._purchasesPaginationInfo.totalPages;
                const currentPageIndex = this._purchasesPaginationInfo.pageIndex;
                const pageIndex = (currentPageIndex + 1) % totalPages;
                await this._loadAndDrawPurchasesPageAsync(pageIndex);
            },
            previousPurchasesPageAsync: async () => {
                const totalPages = this._purchasesPaginationInfo.totalPages;
                const currentPageIndex = this._purchasesPaginationInfo.pageIndex;
                const pageIndex = currentPageIndex === 0 ? totalPages - 1 : currentPageIndex - 1;
                await this._loadAndDrawPurchasesPageAsync(pageIndex);
            },
            openPurchasesPageAsync: async (pageIndex, _filter, _sorter) => {
                // this._lobbyMode = "Videos";
                // this._lobbyVideosMode = "Purchased";
                const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
                lobbyPageParams.lobbyMode = this._lobbyMode;
                lobbyPageParams.lobbyVideosMode = this._lobbyVideosMode;
                // lobbyPageParams.videosPageIndex = pageIndex;
                // lobbyPageParams.videosFilter = filter;
                // lobbyPageParams.videosSorter = sorter;
                this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
                await this._loadAndDrawPurchasesPageAsync(pageIndex);
            },
            getFilteredGalleryTagCodes: () => {
                return this.appController.sdk.galleryManager.filter.tags?.split(",") ?? [];
            },
            addGalleryTag: (code) => {
                if (code.trim() === "")
                    return;
                const filter = this.appController.sdk.galleryManager.filter;
                let tags = filter.tags ?? "";
                if (tags !== "")
                    tags += ",";
                tags += code;
                this._isUpdatingGalleryFilter = true;
                filter.tags = tags;
            },
            removeGalleryTag: (code) => {
                const filter = this.appController.sdk.galleryManager.filter;
                const tagsArray = filter.tags?.split(",") ?? [];
                const newTagsArray = tagsArray.filter(x => x !== code);
                const newTags = newTagsArray.join(",");
                if (newTags !== filter.tags) {
                    this._isUpdatingGalleryFilter = true;
                    filter.tags = newTags;
                }
            },
            removeAllGalleryTags: () => {
                const filter = this.appController.sdk.galleryManager.filter;
                if (filter.tags) {
                    this._isUpdatingGalleryFilter = true;
                    this.appController.sdk.galleryManager.filter.tags = null;
                }
            },
            getGalleryModel: () => {
                const filter = this.appController.sdk.galleryManager.filter;
                if (filter.modelId === null)
                    return null;
                return filter.studioModel ?? null;
            },
            setGalleryModel: (studioModel) => {
                const filter = this.appController.sdk.galleryManager.filter;
                if (studioModel?.modelId !== filter.modelId) {
                    this._isUpdatingGalleryFilter = true;
                    filter.studioModel = studioModel;
                    filter.modelId = studioModel === null ? null : studioModel.modelId;
                }
            },
            getGalleryFilter: () => {
                return this.appController.sdk.galleryManager.filter;
            },
            firstModelsPageAsync: async () => {
                const newPageIndex = 0;
                await this._loadAndDrawModelsPageAsync(newPageIndex);
            },
            previousModelsPageAsync: async () => {
                const newPageIndex = this._modelsPaginationInfo.pageIndex > 0
                    ? this._modelsPaginationInfo.pageIndex - 1
                    : this._modelsPaginationInfo.totalPages - 1;
                await this._loadAndDrawModelsPageAsync(newPageIndex);
            },
            nextModelsPageAsync: async () => {
                const newPageIndex = this._modelsPaginationInfo.pageIndex < this._modelsPaginationInfo.totalPages - 1
                    ? this._modelsPaginationInfo.pageIndex + 1
                    : 0;
                await this._loadAndDrawModelsPageAsync(newPageIndex);
            },
            lastModelsPageAsync: async () => {
                const newPageIndex = this._modelsPaginationInfo.totalPages - 1;
                await this._loadAndDrawModelsPageAsync(newPageIndex);
            },
            // filterByModelAsync: async (studioModel: StudioModel): Promise<void> => {
            //     this.appController.sdk.galleryManager.filter.modelId = studioModel.modelId;
            // },
            openBroadcastAsync: async (params) => {
                if (!this.isVisible || !this._isReady ||
                    this._isLoadingLivePage || this._isLoadingVideosPage || this._isLoadingPurchasesPage ||
                    this._isUpdatingGalleryFilter ||
                    (params.broadcastId === undefined && params.modelNickname === undefined)) {
                    return;
                }
                this.appController.scenesSwitcher.setBroadcastPageParams(params);
                await this.appController.scenesSwitcher.openBroadcastAsync(params);
                if (this._thumbnails !== null) {
                    this._thumbnails.dispose();
                    this._thumbnails = null;
                }
            },
            topUpAsync: async () => {
                await AppView.getInstance().topUpAsync();
            },
            loadCategoriesAsync: async () => {
                return await this.appController.sdk.catalogManager.loadCategoriesAsync();
            },
            updateCurrentPostersAsync: async () => {
                const currentPosters = await this.appController.sdk.catalogManager.loadCurrentPostersAsync();
                //TODO DZZ Make a Jira task!
                // A patch to fill broadcastStatus.
                // broadcastStatus should be in BSS `/poster/current` response.
                const allPosters = [];
                for (let poster of currentPosters.show)
                    allPosters.push(poster);
                for (let poster of currentPosters.highlight)
                    allPosters.push(poster);
                const fillBroadcastStatus = async (poster) => {
                    const broadcast = await this.appController.sdk.catalogManager.loadBroadcastAsync(poster.broadcastId, { showHidden: true, includeFavorites: false });
                    poster.broadcastStatus = broadcast.broadcastStatus;
                    // (poster as any).broadcastStatus = BroadcastApiStatus.PUBLIC; //TODO DZZ DCVR-4765 REMOVE!!! ++++++
                };
                await Promise.all(allPosters.map(poster => fillBroadcastStatus(poster)));
                await this.view?.updatePostersAsync(currentPosters);
            },
            loadEnvironmentDataAsync: async () => {
                return await this.appController.environmentController.loadEnvironmentDescriptorsAsync();
            },
            saveLobbyEnvironmentAsync: async (envs, descriptor) => {
                await this.appController.environmentController.setLobbyEnvironmentAsync(undefined, envs, descriptor);
            },
            markEnvironmentCodeAsSeen: (envs, code) => {
                this.appController.environmentController.markEnvironmentCodeAsSeen(envs, code);
            },
            addModelToFavoritesAsync: async (modelId) => {
                await this.appController.sdk.clientProfileManager.favorites.addModelAsync(modelId);
            },
            removeModelFromFavoritesAsync: async (modelId) => {
                await this.appController.sdk.clientProfileManager.favorites.removeModelAsync(modelId);
            },
            notifyViewsOnIsFavoriteChanged: (modelId, isFavorite) => {
                this.appController.messengerCore.overwriteIsFavorite(modelId, isFavorite);
                this.view?.outputIsFavorite(modelId, isFavorite);
            },
            loadFavoriteModelsAsync: async () => {
                return await this.appController.sdk.clientProfileManager.favorites.loadModelsAsync();
            },
        };
        this._videoclipsFilteredSuccessfully = true;
        this._lastSuccessfulVideoclipsFilter = appController.sdk.galleryManager.filter.get();
        this._initLobbyMode();
    }
    _initLobbyMode() {
        const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams ?? {};
        this._lobbyMode = lobbyPageParams.lobbyMode ?? "Live";
        this._lobbyVideosMode = lobbyPageParams.lobbyVideosMode ?? "AllVideos";
        lobbyPageParams.lobbyMode = this._lobbyMode;
        lobbyPageParams.lobbyVideosMode = this._lobbyVideosMode;
        this.appController.scenesSwitcher.setLobbyPageParams(lobbyPageParams);
    }
    _subscribeAuth() {
        this._authListeners.push(this.appController.sdk.authManager.registerListener("loggedIn", this._loginChanged), this.appController.sdk.authManager.registerListener("loggedOut", this._loginChanged), this.appController.sdk.authManager.registerListener("incompleteSessionFound", this._tryToRestorePrivateShowAsync));
    }
    _unsubscribeAuth() {
        this.appController.sdk.authManager.unregisterListeners(this._authListeners);
    }
    _subscribeCatalog() {
        this._catalogListeners.push(this.appController.sdk.catalogManager.registerListener("onFilterChanged", this._liveFilterChanged), this.appController.sdk.catalogManager.registerListener("onPageChanged", this._livePageChanged));
        this._galleryListeners.push(this.appController.sdk.galleryManager.registerListener("onFilterChanged", this._videosFilterChanged), this.appController.sdk.galleryManager.registerListener("onPageChanged", this._videosPageChanged));
    }
    _unsubscribeCatalog() {
        this.appController.sdk.catalogManager.unregisterListeners(this._catalogListeners);
        this.appController.sdk.galleryManager.unregisterListeners(this._galleryListeners);
    }
    async initViewCallbacksAsync(view) {
        // this.appController.environmentController.attachView(view.getEnvironmentView());
        await view.initAsync(this._sceneCallbacks);
    }
    async showAsync() {
        if (this.isVisible)
            return;
        const appView = AppView.getInstance();
        const settings = Settings.getInstance();
        if (!settings.onboarding.wasViewed) {
            await appView.onboardingPanel.prepareAsync();
        }
        else {
            if (!settings.ptAnnouncement.wasViewed) {
                await appView.announcementPtPanel.prepareAsync();
                // if (!settings.messengerAnnouncement.wasViewed) {
                //     await appView.announcementMessengerPanel.prepareAsync();
                // if (!settings.vodAnnouncement.wasViewed) {
                //     await appView.announcementVodPanel.prepareAsync();
                // } else if (!settings.favoritesAnnouncement.wasViewed) {
                //     await appView.announcementFavoritesPanel.prepareAsync();
            }
        }
        this._initLobbyMode();
        await super.showAsync();
        await appView.xmasCalendar.preloadAsync();
        appView.xmasCalendar.inLobbyScene = true;
        if (this._isLoadingOnShow)
            return;
        this._isLoadingOnShow = true;
        try {
            // this._currentPageIndex = this.appController.sdk.catalogManager.pagination?.pageIndex ?? 0;
            const envs = await this.appController.environmentController.loadEnvironmentDescriptorsAsync();
            try {
                await this._updateLoginSection();
            }
            catch (e) {
                console.error(e.message);
            }
            const results = await Promise.allSettled([
                this.appController.environmentController.setLobbyEnvironmentAsync(appView.environmentView, envs),
                this._loadCurrentLivePageAsync(),
                this._loadCurrentVideosPageAsync(),
                this._loadCurrentPurchasesPageAsync(),
                this._loadModelsAsync(),
                this._loadSelectedModelInfoAsync(),
                // this._updateLoginSection()
            ]);
            results.forEach((x, i) => {
                let errorSource = "Unknown source:";
                if (i === 0)
                    errorSource = "Error in setLobbyEnvironmentAsync():";
                else if (i === 1)
                    errorSource = "Error in _loadCurrentLivePageAsync():";
                else if (i === 2)
                    errorSource = "Error in _loadCurrentVideosPageAsync():";
                else if (i === 3)
                    errorSource = "Error in _loadCurrentPurchasesPageAsync():";
                else if (i === 4)
                    errorSource = "Error in _loadModelsAsync():";
                else if (i === 5)
                    errorSource = "Error in _loadSelectedModelInfoAsync():";
                // else if (i === 6) errorSource = "Error in _updateLoginSection():";
                if (x.status === "rejected")
                    console.error(errorSource, x.reason);
            });
            this._subscribeAuth();
            this._subscribeCatalog();
        }
        finally {
            this._isLoadingOnShow = false;
        }
        this._isReady = true;
        appView.fadingHelper?.startFadeIn(true);
    }
    async hideAsync() {
        this._isReady = false;
        AppView.getInstance().xmasCalendar.inLobbyScene = false;
        await super.hideAsync();
        this._unsubscribeCatalog();
        this._unsubscribeAuth();
    }
    static _recalculateModelsPaginationInfo(pageSize, newPageIndex, studioModels) {
        if (pageSize <= 0)
            pageSize = 1;
        let totalPages = (studioModels.totalCount % pageSize === 0)
            ? studioModels.totalCount / pageSize
            : Math.floor(studioModels.totalCount / pageSize) + 1;
        if (totalPages === 0)
            totalPages = 1;
        if (newPageIndex < 0)
            newPageIndex = totalPages - 1;
        else if (newPageIndex > totalPages - 1)
            newPageIndex = 0;
        return {
            totalItems: studioModels.totalCount,
            pageSize,
            itemsOnPage: studioModels.pageItems.length,
            totalPages,
            pageIndex: newPageIndex,
        };
    }
    async _loadLivePageDataAsync(pageIndex, pageSize) {
        if (this._thumbnails === null || this._thumbnails.pageSize !== pageSize) {
            this._thumbnails = new BroadcastThumbnails(pageSize, globalOptions.thumbnailPhotoTypeDefault, "default", {
                sdk: this.appController.sdk,
                intervalInMillis: Scene._RELOAD_INTERVAL_MILLIS,
                pageReloadMode: PageReloadMode.BROADCASTS_ON_PAGE
            });
        }
        await this.appController.sdk.catalogManager.loadPageAsync(pageIndex, pageSize, this._thumbnails, {
            showOffline: this.appController.showOfflineBroadcasts,
            showHidden: this.appController.showHiddenBroadcasts,
            includeFavorites: true,
        });
        if (this._thumbnails.itemsOnPage === 0 && this._thumbnails.totalBroadcastCount > 0) {
            const lastPageIndex = this._thumbnails.totalPages - 1;
            await this.appController.sdk.catalogManager.loadPageAsync(lastPageIndex, pageSize, this._thumbnails, {
                showOffline: this.appController.showOfflineBroadcasts,
                showHidden: this.appController.showHiddenBroadcasts,
                includeFavorites: true,
            });
        }
    }
    async _loadAndDrawModelsPageAsync(pageIndex) {
        if (this._lastModelsPageIndex === pageIndex)
            return;
        this._lastModelsPageIndex = pageIndex;
        const pageSize = this.view?.getModelsOnPage() ?? 1;
        const studioModels = await this.appController.sdk.galleryManager.loadModelsPageAsync(pageIndex, pageSize);
        this._modelsPaginationInfo = LobbyScene._recalculateModelsPaginationInfo(pageSize, pageIndex, studioModels);
        this.view?.updateModelsPage(studioModels);
        this.view?.updateModelsPageInfo(this._modelsPaginationInfo);
    }
}
